<template>
    <Page class="organization-home-page">
        <div class="welcome-header">
            <h1>Welcome to <MyStekzLogo class="logo" /></h1>
            <p class="subtitle">
                {{
                    hasSteks
                        ? "Your automation workspace dashboard"
                        : "Let's get you started with your automation journey"
                }}
            </p>
        </div>

        <!-- Existing Steks section -->
        <div v-if="hasSteks" class="cards-container">
            <Card
                v-for="stek in steks"
                :key="stek.identity"
                class="navigation-card stek-card"
                @click="navigateToStek(stek)"
            >
                <template #header>
                    <Avatar
                        v-if="stek.logo"
                        :image="stek.logo.preview"
                        class="stek-logo"
                    />
                    <Avatar
                        v-else
                        :label="getInitials(stek.name)"
                        size="xlarge"
                        class="stek-logo-placeholder"
                    />
                </template>
                <template #title>{{ stek.name }}</template>
                <template #subtitle v-if="stek.fqdn">{{ stek.fqdn }}</template>
            </Card>

            <!-- Add new Stek card -->
            <Card
                class="stek-card navigation-card add-stek"
                @click="openCreateStekModal"
            >
                <template #header>
                    <Avatar icon="pi pi-plus" size="xlarge" />
                </template>
                <template #title>Create New Stek</template>
            </Card>
        </div>

        <!-- Quick actions when no Steks exist or as additional actions when they do -->
        <div class="cards-container">
            <Card
                v-if="!hasSteks"
                class="navigation-card quick-action-card primary-action"
                @click="openCreateStekModal"
            >
                <template #content>
                    <div class="card-content-wrapper">
                        <Avatar
                            icon="pi pi-plus-circle"
                            size="xlarge"
                            class="card-icon primary-icon"
                        />
                        <div class="card-content">
                            <h3>Create your first Stek</h3>
                            <p>
                                A Stek is your collaborative workspace for
                                automation
                            </p>
                        </div>
                    </div>
                </template>
            </Card>

            <Card
                class="navigation-card quick-action-card secondary-action"
                @click="navigateToHelp"
            >
                <template #content>
                    <div class="card-content-wrapper">
                        <Avatar
                            icon="pi pi-question-circle"
                            size="xlarge"
                            class="card-icon secondary-icon"
                        />
                        <div class="card-content">
                            <h3>Learn about MyStekz</h3>
                            <p>
                                Explore documentation and tutorials to get
                                started
                            </p>
                        </div>
                    </div>
                </template>
            </Card>

            <Card
                v-if="canCreateNewUsers"
                class="navigation-card quick-action-card tertiary-action"
                @click="navigateToUserManagement"
            >
                <template #content>
                    <div class="card-content-wrapper">
                        <Avatar
                            icon="pi pi-users"
                            size="xlarge"
                            class="card-icon tertiary-icon"
                        />
                        <div class="card-content">
                            <h3>Invite team members</h3>
                            <p>
                                Collaborate with your team on automation
                                projects
                            </p>
                        </div>
                    </div>
                </template>
            </Card>
        </div>

        <Card v-if="!hasSteks" class="next-steps">
            <template #title>
                <h2>MyStekz workflow</h2>
            </template>
            <template #content>
                <div class="workflow-steps">
                    <div
                        class="step"
                        v-for="(step, index) in workflowSteps"
                        :key="index"
                    >
                        <Avatar
                            :label="(index + 1).toString()"
                            size="large"
                            class="step-number"
                        />
                        <div class="step-description">
                            <h3>{{ step.title }}</h3>
                            <p>{{ step.description }}</p>
                        </div>
                    </div>
                </div>
            </template>
        </Card>
    </Page>
</template>

<script setup lang="ts">
import { computed } from "vue";
import router from "@/router";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useSteksManager, Stek } from "@/stores/steks-store";
import MyStekzLogo from "../assets/mystekz-logo.svg?component";
import Avatar from "primevue/avatar";
import Page from "@/components/Page.vue";
import { ClusterRole, useUserManager } from "@/stores/user-store";

// Get steks data
const { steks } = useSteksManager();
const { currentOrganizationRole } = useUserManager();
const hasSteks = computed(() => steks.value && steks.value.length > 0);
const canCreateNewUsers = computed(() => {
    return currentOrganizationRole.value === ClusterRole.USER_MANAGER;
});

const { organization } = useOrganizationManager();

// Workflow steps data
const workflowSteps = [
    {
        title: "Create a Stek",
        description: "A central workspace for your organization",
    },
    {
        title: "Add domains",
        description: "Organize your work into functional areas",
    },
    {
        title: "Create products",
        description: "Define the products you'll work with",
    },
    {
        title: "Define processes",
        description: "Build automation workflows for your business",
    },
];

// Action handlers
const openCreateStekModal = () => {
    if (organization.value?.name) {
        router.push({
            name: "createStek",
            params: {
                orgName: organization.value.name,
            },
        });
    }
};

const navigateToHelp = () => {
    router.push({ name: "help" });
};

const navigateToUserManagement = () => {
    if (organization.value?.name) {
        router.push({
            name: "userManagement",
            params: {
                orgName: organization.value.name,
            },
        });
    }
};

// Helper functions for Stek cards
const navigateToStek = (stek: Stek) => {
    if (organization.value?.name) {
        router.push({
            name: "stekPage",
            params: {
                orgName: organization.value.name,
                stekName: stek.name,
            },
        });
    }
};

const getInitials = (name: string) => {
    return name
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase())
        .join("")
        .substring(0, 2);
};
</script>

<style lang="scss" scoped>
.organization-home-page {
    max-width: 1200px;

    .welcome-header {
        text-align: center;
        margin-bottom: var(--default-gap);

        h1 {
            font-size: var(--text-xxl);
            display: flex;
            align-items: baseline;
            justify-content: center;
            gap: 0.5rem;
        }

        .logo {
            height: 1em;
            width: auto;
        }

        .subtitle {
            color: var(--text-color-secondary);
            margin: 0;
        }
    }

    .navigation-card {
        cursor: pointer;
        transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;

        &:hover {
            transform: translateY(-1px);
            background-color: var(--surface-background-mild);
        }
    }

    .stek-card {
        flex-basis: 200px;
        max-width: 200px;
        flex-grow: 0;
        border-bottom: var(--stekz-border-width) solid
            var(--stekz-primary-color);

        :deep(.p-card-header) {
            aspect-ratio: 1 / 1;
            display: flex;
        }

        :deep(.p-card-title) {
            overflow: hidden;
            text-overflow: ellipsis;
        }

        :deep(.p-avatar) {
            width: 100%;
            height: 100%;
        }
        .stek-logo {
            margin: auto;
            height: auto;
        }
    }

    .cards-container {
        display: flex;
        flex-wrap: wrap;
        gap: var(--default-gap);
        margin-bottom: var(--default-gap);
        max-width: 100%;
    }

    .quick-action-card {
        border-left: var(--stekz-border-width) solid transparent;
        flex: 1;

        :deep(.p-avatar) {
            aspect-ratio: 1 / 1;
            background-color: transparent;
        }

        .card-content-wrapper {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 1rem;
        }

        &.primary-action {
            border-left-color: var(--stekz-primary-color);

            .primary-icon {
                // background-color: var(--stekz-primary-color);
                color: var(--stekz-primary-color);
                // color: var(--stekz-white);
            }
        }

        &.secondary-action {
            border-left-color: var(--stekz-secondary-color);

            .secondary-icon {
                color: var(--stekz-secondary-color);
            }
        }

        &.tertiary-action {
            border-left-color: var(--stekz-tertiary-color);

            .tertiary-icon {
                color: var(--stekz-tertiary-color);
            }
        }
    }

    .next-steps {
        :deep(.p-card-title) {
            // font-size: 1.5rem;
            text-align: center;
            // color: var(--text-color);
        }

        :deep(.p-card-content) {
            padding: 1.5rem;
        }

        .workflow-steps {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 1.5rem;
        }

        .step {
            display: flex;
            align-items: flex-start;
            gap: 1rem;

            .step-number {
                background-color: var(--stekz-primary-color);
                color: var(--stekz-white);
                aspect-ratio: 1 / 1;
            }

            .step-description {
                h3 {
                    margin: 0 0 0.5rem 0;
                    color: var(--text-color);
                    font-size: 1.2rem;
                }

                p {
                    margin: 0;
                    color: var(--text-color);
                }
            }
        }
    }
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
    .organization-home-page {
        padding: 0.5rem;

        .welcome-header h1 {
            font-size: 1.5rem;
        }

        .next-steps .workflow-steps {
            grid-template-columns: 1fr;
        }
    }
}
</style>
